import {
  USERS_PAGINATED_FETCH_STARTED,
  USERS_PAGINATED_FETCH_SUCCEEDED,
  USERS_PAGINATED_FETCH_FAILED,
  USERS_PAGINATED_CHANGE_PAGE,
  USERS_PAGINATED_CHANGE_ITEMS_PER_PAGE,
  USERS_PAGINATED_CHANGE_ORDER,
  USERS_PAGINATED_CHANGE_ORDER_BY,
  USERS_PAGINATED_CHANGE_ORDER_AND_ORDER_BY,
  USERS_PAGINATED_CLEAR
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { getRequestOptions, getURLParameter } from '#helper';

let abortController = new AbortController();

export const fetchPaginatedUsers = () => async (dispatch, getState) => {
  try {
    abortController.abort();
    dispatch({
      type: USERS_PAGINATED_FETCH_STARTED
    });

    const {
      page,
      order,
      orderBy,
      filterBy,
      itemsPerPage
    } = getState().usersPaginated;
    abortController.abort();
    const limit = itemsPerPage;
    const p = getURLParameter('page') || page;
    const offset = (Number(p) - 1) * itemsPerPage;
    const baseUrl = webPortalApi.buildApiUrl('users');
    const searchParams = new URLSearchParams({ order, orderBy, offset, limit, pagination: 1 });
    const { user } = getState().listFilter;

    const searchValue = user?.search?.value;
    const unexpiredValue = user?.unexpired?.value;
    const projectValue = user?.project?.value;

    if (searchValue) {
      searchParams.set('filter', searchValue);
      searchParams.set('filterBy', filterBy);
    }

    if (projectValue) {
      const ids = projectValue.map(v => v.data.id).join(',');

      if (ids) {
        searchParams.set('project', ids);
      }
    }

    if (unexpiredValue) {
      const [unexpiredData] = user.unexpired.value;
      const unexpired = unexpiredData.data.value;
      if (unexpired === '1') { // Active only
        searchParams.set('active', unexpired);
      } else {
        searchParams.delete('active');
      }
    }

    // if (isPaginationFilterExist && (!userIds && !projectsIds)) {
    //   return dispatch({
    //     type: USERS_PAGINATED_FETCH_SUCCEEDED,
    //     payload: []
    //   });
    // }

    const usersPaginationURL = `${baseUrl}?${searchParams.toString()}`;

    abortController = new AbortController();
    const { signal } = abortController;
    // Debounce to do not send multiple requests in short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });
    const res = await fetch(usersPaginationURL, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request'
        })
      );
    }

    const { users = [], last } = await res.json();

    if (users.length) {
      const usersIds = users.map(u => u.id).join(',');
      const usersDirectProjects = users.map(u => u.directProjects).flat();
      const uniqueUserProjects = [...new Set(usersDirectProjects)].join(',');

      const requests = [
        fetch(webPortalApi.buildApiUrl(`device/count?user=${usersIds}`), getRequestOptions()),
        fetch(webPortalApi.buildApiUrl(`project/?ids=${uniqueUserProjects}`), getRequestOptions())
      ];

      const responses = await Promise.all(requests);

      const contentTypes = responses.map(r => r.headers.get('content-type'));
      if (contentTypes.some(ct => (!ct || !ct.includes('application/json')))) {
        throw new TypeError('Response is not json');
      }

      const notOkResponses = responses.filter(r => !r.ok);
      if (notOkResponses.length) {
        throw await Promise.all(notOkResponses.map(r => r.json()));
      }

      const [
        deviceCount,
        projects
      ] = await Promise.all(responses.map(r => r.json()));

      return dispatch({
        type: USERS_PAGINATED_FETCH_SUCCEEDED,
        payload: {
          users,
          projects,
          deviceCount
        },
        totalCount: last
      });
    }

    return dispatch({
      type: USERS_PAGINATED_FETCH_SUCCEEDED,
      payload: users,
      totalCount: last
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return false;
    }
    return dispatch({
      type: USERS_PAGINATED_FETCH_FAILED,
      error: e
    });
  }
};

export const changePage = (newPage, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: USERS_PAGINATED_CHANGE_PAGE,
    payload: newPage
  });

  if (refetch) {
    dispatch(fetchPaginatedUsers());
  }
};

export const changeItemsPerPage = (newItemsPerPage, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: USERS_PAGINATED_CHANGE_ITEMS_PER_PAGE,
    payload: newItemsPerPage
  });

  if (refetch) {
    dispatch(fetchPaginatedUsers());
  }
};

export const changeOrder = (newOrder, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: USERS_PAGINATED_CHANGE_ORDER,
    payload: newOrder
  });

  if (refetch) {
    dispatch(fetchPaginatedUsers());
  }
};

export const changeOrderBy = (newOrderBy, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: USERS_PAGINATED_CHANGE_ORDER_BY,
    payload: newOrderBy
  });

  if (refetch) {
    dispatch(fetchPaginatedUsers());
  }
};

export const changeOrderAndOrderBy = (
  newOrder,
  newOrderBy,
  { refetch = true } = {}
) => dispatch => {
  dispatch({
    type: USERS_PAGINATED_CHANGE_ORDER_AND_ORDER_BY,
    payload: { order: newOrder, orderBy: newOrderBy }
  });

  if (refetch) {
    dispatch(fetchPaginatedUsers());
  }
};

export const clearPaginatedUsers = () => dispatch => {
  abortController.abort();
  dispatch({
    type: USERS_PAGINATED_CLEAR
  });
};
